// src/workspace/WorkspaceLandingPage.jsx
//
// The ready room. DESIGN_analyst_workspace §4.
//
// ── ORIENT AND RELEASE, NOT A DASHBOARD ──────────────────────────────────────
//
// A dashboard invites reading. This is read once, at the start of a session,
// and its job is to answer "what should I do now" fast enough that the answer
// is acted on rather than studied. That is why the primary action is the first
// thing in the DOM and the largest thing on the page, and why every panel below
// it is a count with an entry point rather than a chart.
//
// No charts here, deliberately — §4's "not in this step", and more durably
// because a chart is something to look at rather than something to act on.
//
// ── WHAT IS ABSENT, AND WHY IT STAYS ABSENT ──────────────────────────────────
//
// No rate. Not decisions/hour, not time-on-target. The heartbeat has no data
// from real review yet, and a rate computed from dev-session traffic is what
// produced the 105-approval number that had to be retracted.
//
// No quality metrics, ever — §6. Not override rate, not flag rate, not peer
// agreement, not even self-facing. An analyst who believes they are measured
// flags less and spends less on Tier C, which is precisely the behaviour that
// corrupts both the measurement and the output. The A′ override rate DOES
// appear, because it is a property of the 3× rule under calibration rather than
// a score for the person, and it is labelled as such.
//
// No per-person breakdown of the team. With one analyst it is harmless; with
// two it is a leaderboard. /stats returns per-analyst throughput and this page
// deliberately reduces it to a count of active reviewers before rendering.
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight, AlertTriangle, Hand, CheckCircle2, Loader2,
} from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { useAuthStore } from "../stores/useAuthStore";
import { useEffectiveOrg } from "../hooks/useEffectiveOrg";
import { TOOLS as BASE_TOOLS, canSeeTool } from "./tools";

// ── THE TOOL REGISTRY ───────────────────────────────────────────────────────
//
// One entry per internal tool. Adding a third is a CARD DEFINITION, not a
// routing change and not another decision about where it goes -- which is the
// whole reason this list exists rather than two hand-written blocks.
//
// ACCESS IS PER-CARD, BY REALM ROLE. A user holding one role sees one card, not
// an error. Before this the landing fetched WellFinder's two endpoints
// unconditionally and failed WHOLESALE if either 403'd -- so a feature_reviewer
// who did not also hold wellfinder_reviewer landed on "Could not load your
// workspace", which was true of the page and false of their access.
//
// The role check here is for RENDERING ONLY. Every endpoint enforces it again
// server-side; a client that lied about its roles would get 403s, not data.
// Card bodies live here because they are React components and the header, which
// reads the same registry for a tool's NAME, has no use for them. Keyed by id so
// a tool without a body simply has no entry.
const CARD_BODIES = { wellfinder: WellfinderCardBody };

// Identity comes from workspace/tools.js -- one owner for what a tool is called
// and where it lives, so the shell header cannot disagree with the card.
const TOOLS = BASE_TOOLS.map((t) => ({ ...t, Body: CARD_BODIES[t.id] ?? null }));


const TIER_LABEL = { a: "A", a_prime: "A′", b: "B", c: "C", d: "D" };

// The order the landing narrates tiers in. NOT the queue's sort order, which
// puts C first because it is the differentiated product. Here the reader is
// orienting, and A-then-C reads as "the bulk, then the interesting part".
const TIER_ORDER = ["a", "a_prime", "b", "c"];

/** A first-timer should not land on Tier C even though the queue sorts it first. */
const FIRST_SESSION_TIER = "a";

export default function WorkspaceLandingPage() {
  const roles = useAuthStore((st) => st.roles);
  const orgId = useEffectiveOrg()?.id ?? null;
  const visibleTools = useMemo(
    () => TOOLS.filter((t) => canSeeTool(t, { roles, orgId })),
    [roles, orgId],
  );
  const canWellfinder = visibleTools.some((t) => t.id === "wellfinder");

  const [toolStats, setToolStats] = useState({});
  const [stats, setStats] = useState(null);
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // ONE REQUEST PER TOOL THE USER CAN SEE, in parallel, and none for tools
      // they cannot. At two this is two requests, which is what the page
      // already did; at six it is six, and that is the point at which a single
      // GET /api/v1/workspace/summary earns itself. The tool stats endpoints
      // are shaped to fold into one -- see the feature adjudicator's, which
      // returns a `tool` object for exactly that reason.
      //
      // A TOOL THAT FAILS DOES NOT TAKE THE PAGE DOWN. Each settles
      // independently, so one 403 or one slow endpoint costs its own card and
      // nothing else. That is the defect this replaced: both WellFinder calls
      // were required and either failing rendered a whole-page error.
      const settled = await Promise.allSettled(
        visibleTools.map((t) => fetchWithAuth(t.stats).then((r) =>
          r.ok ? r.json() : Promise.reject(new Error(`${t.id} ${r.status}`)))),
      );
      if (cancelled) return;
      const next = {};
      settled.forEach((res, i) => {
        const t = visibleTools[i];
        next[t.id] = res.status === "fulfilled"
          ? { data: res.value, error: null }
          : { data: null, error: "Unavailable" };
        if (res.status === "rejected") console.error("[workspace/landing]", t.id, res.reason);
      });
      setToolStats(next);
      if (next.wellfinder?.data) setStats(next.wellfinder.data);

      // The WellFinder deep-dive sections below need me/summary, which only
      // that tool has. Fetched only when its card is visible, so a
      // feature-only reviewer never issues a request they cannot make.
      if (canWellfinder) {
        try {
          const mRes = await fetchWithAuth("/api/v1/wellfinder/me/summary");
          if (!cancelled && mRes.ok) setMe(await mRes.json());
        } catch (e) { console.error("[workspace/landing] me/summary", e); }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [visibleTools, canWellfinder]);

  // Remaining per tier, from the corpus-wide breakdown. Pending only: a flagged
  // record is not remaining work for the person reading this.
  // by_tier_status is an OBJECT keyed by tier — { a: { pending: 6644 }, … } —
  // not an array of rows. Worth stating: the array shape is the obvious guess
  // and `for…of` over an object throws rather than yielding nothing, so the
  // wrong guess blanks the whole page rather than one panel.
  // Suggested tier for a cold start: the first tier in narration order that has
  // anything left. A first session is pinned to A regardless. This used to drive
  // a separate "Start reviewing" button; it now drives the WellFinder card's own
  // link, because the card IS the primary action and two of them competed.
  const remaining = useMemo(() => {
    const out = {};
    for (const [tier, byStatus] of Object.entries(stats?.by_tier_status ?? {})) {
      out[tier] = byStatus?.pending ?? 0;
    }
    return out;
  }, [stats]);


  // COUNT ONLY. /stats returns throughput.analysts as a per-person array with
  // decisions and a per-hour rate on each; reducing it to a length here is
  // deliberate and is the whole of §4's "never a per-person breakdown". The
  // array is not passed to any child, so there is nothing further down that
  // could render it by accident.
  const suggestedTier = useMemo(() => {
    if (!me?.has_history) return FIRST_SESSION_TIER;
    return TIER_ORDER.find((t) => (remaining[t] ?? 0) > 0) ?? FIRST_SESSION_TIER;
  }, [me, remaining]);

  const activeReviewers = stats?.throughput?.analysts?.length ?? null;

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="animate-spin text-fg-secondary" size={18} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-16">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  const claims = me?.claims ?? [];
  const totals = stats?.totals ?? {};
  const cal = stats?.aprime_calibration ?? {};

  return (
    // ── TWO COLUMNS, AND THE ASYMMETRY IS THE POINT ────────────────────────
    //
    // §4 opens "not a dashboard", and that constraint is about CONTENT: no
    // charts, no rates, no quality metrics, nothing to study. None of that
    // changed here. What changed is that the page uses the width it has.
    //
    // Left is what you DO — the primary action and the things waiting on you.
    // Right is what is TRUE — corpus progress, your counts, the team's, the
    // calibration. Reference material, read on arrival and then ignored.
    //
    // The split is deliberately uneven. An equal-weight two-column layout would
    // put status beside the primary action at the same visual rank, and the one
    // thing this page must not become is a screen where "start reviewing" is
    // one of six things competing for attention. Left takes the flexible
    // column, right is a fixed rail that does not grow on a wide monitor.
    //
    // Single column below lg. The review surface's map has a 480px floor and
    // this workspace is used on laptops; a rail that squeezes the left column
    // to nothing is worse than stacking.
    <div className="h-full w-full overflow-y-auto px-8 py-10">
      {/* ── Your tools ─────────────────────────────────────────────────────── */}
      {/* First, because "which tool" precedes "what in it". A reviewer holding
          one role sees one card and no evidence that the other exists. */}
      <Section title="Your tools">
        {visibleTools.length === 0 ? (
          // NOT an error. A user with neither role has no work here, which is a
          // fact about their access and not a failure of the page -- and saying
          // "could not load" would send them to support over a correct state.
          <p className="text-xs text-fg-secondary">
            No review tools are assigned to you. Ask an administrator if you expect one.
          </p>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2">
            {visibleTools.map((t) => (
              <ToolCard
                key={t.id}
                tool={t}
                state={toolStats[t.id]}
                loading={loading}
                ctx={{ me, stats, totals, remaining, suggestedTier }}
              />
            ))}
          </div>
        )}
      </Section>

      {/* ── What needs you ─────────────────────────────────────────────────── */}
      {/* The cards carry the COUNTS. This carries the things you click: the
          supervisor flagged-assignment path, and one row per reopened record
          linking at ?record= rather than at a filter that would return the whole
          pending queue with the reopened ones somewhere inside it.

          It stays a full-width block above the reference row because it is the
          only thing below the cards anyone acts on. */}
      {canWellfinder && (
      <div className="mt-8 max-w-2xl">
      {/* ── What needs you ────────────────────────────────────────────────── */}
      <Section title="What needs you">
      {/* Supervisors only. This is the assignment mechanism needs_review has
          never had — the records were always findable under the status
          filter; nothing routed them to anybody. */}
      {me?.is_supervisor && (
        <NeedsYouRow
          icon={<AlertTriangle size={14} className="text-amber-400" />}
          label="Flagged, awaiting your review"
          count={me.flagged_awaiting}
          // Straight to the review surface with the status filter on, not to
          // a list of its own. A separate page was a screen whose every row's
          // only action was "open this in the review surface" — it had no
          // map, no candidates and no commit bar, so it was a hop. The
          // ASSIGNMENT that §4 says was missing is this panel existing and
          // being somebody's job; it never required a second route.
          to="/workspace/review"
          search={{ status: "needs_review" }}
          cta="Open them"
          empty="Nothing escalated right now."
        />
      )}

      <NeedsYouRow
        icon={<Hand size={14} className="text-fg-secondary" />}
        label="Records you have claimed"
        count={claims.length}
        to="/workspace/review"
        cta="Continue"
        empty="No records claimed."
      />
      </Section>

      {/* ── Reopened by someone else ──────────────────────────────────────── */}
      {/* ITS OWN SECTION, next to "What needs you" rather than inside it. These
        are records where a colleague withdrew your verdict and said why, so
        they do belong on the left with the things that want your attention —
        but they are not a queue you work through, and folding them into that
        list would make three read-once notes look like three claimed records.

        TWO FILTERS DECIDE WHETHER THIS PANEL TELLS THE TRUTH, both server-side:

          review_mode <> 'batch_job'   the 116-record development reset was an
                                       administrative act, not 116 colleagues
                                       overruling someone.
          actor_id <> the caller       a self-undo is not disagreement. Every
                                       reopened record in the corpus was
                                       reopened by its own author minutes after
                                       committing; without this the panel
                                       reported three disagreements that never
                                       happened, and this is why it now reads
                                       zero rather than three.

        It stays a LIST WITH REASONS and must never become a percentage. §6
        forbids showing an analyst peer-agreement metrics, and what separates
        this from one is that it names specific records with stated causes
        instead of scoring the person. */}
      <Section
      title={
        (me?.reopened?.length ?? 0) > 0
          ? `Reopened by someone else · ${fmt(me.reopened.length)}`
          : "Reopened by someone else"
      }
      >
      <ReopenedRows reopened={me?.reopened ?? []} />
      </Section>
      </div>
      )}

      {/* ── WellFinder reference ───────────────────────────────────────────── */}
      {/* What is left once the cards took what decides: own volume, the team's,
          and the calibration. Reference — read on arrival and then ignored — so
          it sits below the cards in one row rather than in a rail beside them.
          The rail put reference material at the same visual rank as the thing
          it was reference FOR. */}
      {canWellfinder && (
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">

      {/* ── Your volume ───────────────────────────────────────────────────── */}
      <Section panel title="Your volume">
        {me?.volume?.total ? (
          <>
            <p className="text-sm">
              <span className="font-medium text-fg-primary">{fmt(me.volume.total)}</span>
              <span className="text-fg-secondary"> records adjudicated</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-fg-secondary">
              {me.volume.by_tier.map((v) => (
                <span key={v.tier}>
                  Tier {TIER_LABEL[v.tier] ?? v.tier}
                  <span className="ml-1.5 text-fg-primary">{fmt(v.records)}</span>
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-fg-secondary">
            Nothing adjudicated yet. Counts appear here as you work.
          </p>
        )}
      </Section>

      {/* ── Team ──────────────────────────────────────────────────────────── */}
      {/* Collective only. Never a per-person breakdown: with two analysts that
          is a leaderboard, and §6's argument about what a leaderboard does to
          flagging and to Tier C does not weaken with team size. */}
      <Section panel title="Team">
        <p className="text-sm text-fg-secondary">
          {fmt(totals.adjudicated)} records adjudicated collectively
          {activeReviewers != null && (
            <> · {activeReviewers} {activeReviewers === 1 ? "reviewer" : "reviewers"} active</>
          )}
        </p>
      </Section>

      {/* ── A′ calibration ───────────────────────────────────────────────── */}
      <AprimeCalibration cal={cal} bulkEnabled={stats?.aprime_bulk_enabled} />

      </div>
      )}
    </div>
  );
}



// ── A′ calibration ───────────────────────────────────────────────────────────

function AprimeCalibration({ cal, bulkEnabled }) {
  if (cal?.reviewed == null) return null;
  const complete = cal.reviewed >= cal.sample_target;

  return (
    <Section panel title="A′ calibration">
      <div className="flex items-baseline gap-2">
        {complete && <CheckCircle2 size={14} className="text-accent-info" />}
        <p className="text-sm">
          <span className="font-medium text-fg-primary">
            {fmt(cal.reviewed)} / {fmt(cal.sample_target)}
          </span>
          {/* Shown as DONE rather than as progress. A progress bar at 117%
              reads as a bar that has not finished. */}
          <span className="text-fg-secondary"> records — {complete ? "complete" : "in progress"}</span>
        </p>
      </div>

      {/* The override rate is a property of the 3× dominance rule, not a score
          for the analyst — labelled so nobody reads it as one. It is the number
          the bulk-confirm decision is made against. */}
      {cal.override_rate != null && (
        <p className="mt-2 text-xs text-fg-secondary">
          Rank-1 overridden on{" "}
          <span className="text-fg-primary">
            {(cal.override_rate * 100).toFixed(1)}%
          </span>{" "}
          of {fmt(cal.matches_set)} matches set — a measurement of the 3× rule,
          not of the reviewer.
        </p>
      )}

      {complete && !bulkEnabled && (
        <p className="mt-2 text-xs text-fg-secondary">
          Bulk confirmation remains disabled.
        </p>
      )}
    </Section>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────

/**
 * Two treatments, one component.
 *
 * The default is a rule and vertical space — right for the left column, where
 * sections are stops in a single downward read and a box around each would
 * fence off the primary action.
 *
 * `panel` boxes it, which is what the rail needs: four unrelated facts stacked
 * in a narrow column read as one long list without frames, and the reader has
 * no way to tell where "your volume" stops and "team" starts.
 */
// ── ToolCard ────────────────────────────────────────────────────────────────
//
// A card must answer one question: is there anything here for me right now. So
// the metrics are the tool's own, formatted by the tool's own entry in TOOLS --
// the card renders whatever it is handed and knows nothing about tiers or cells.
function ToolCard({ tool, state, loading, ctx }) {
  const metrics = state?.data ? tool.metrics(state.data) : [];
  // "Nothing outstanding" is a real answer and a useful one: it tells a
  // reviewer not to open the tool, which is exactly what the card is for.
  const idle = state?.data && metrics.every((m) => !m.value);

  // One body, two containers. A Link when there is somewhere to go, a plain div
  // when there is not -- rather than a Link with a click handler that swallows
  // the navigation, which still shows a URL in the status bar and still offers
  // "open in new tab".
  const Shell = tool.ready ? Link : "div";
  const shellProps = tool.ready
    ? { to: tool.to, search: tool.search ? tool.search(ctx) : undefined, className: "group rounded-lg border border-bg-border bg-white/[0.02] px-4 py-3.5 transition hover:border-cyan-400/40 hover:bg-cyan-400/[0.04]" }
    : { className: "rounded-lg border border-dashed border-bg-border bg-white/[0.01] px-4 py-3.5" };

  return (
    <Shell {...shellProps}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-fg">{tool.label}</div>
          <div className="mt-0.5 text-xs text-fg-secondary">{tool.description}</div>
        </div>
        {tool.ready ? (
          <ArrowRight size={14} className="mt-0.5 shrink-0 text-fg-secondary group-hover:text-cyan-300" />
        ) : (
          <span className="mt-0.5 shrink-0 rounded border border-bg-border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-fg-secondary">
            soon
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {loading && !state && (
          <span className="inline-flex items-center gap-1.5 text-xs text-fg-secondary">
            <Loader2 size={12} className="animate-spin" /> loading
          </span>
        )}
        {/* An unavailable tool says so on its own card and does not claim zero
            work -- "0 deferrals" and "we could not ask" are different facts and
            a reviewer would act differently on each. */}
        {state?.error && (
          <span className="text-xs text-amber-400">{state.error}</span>
        )}
        {state?.data && idle && (
          <span className="inline-flex items-center gap-1.5 text-xs text-fg-secondary">
            <CheckCircle2 size={12} className="text-emerald-400" /> nothing outstanding
          </span>
        )}
        {state?.data && !idle && metrics.map((m) => (
          <span key={m.label} className="text-xs text-fg-secondary">
            <span className="text-base font-semibold tabular-nums text-fg">{m.value}</span>{" "}
            {m.label}
          </span>
        ))}
      </div>

      {tool.Body && state?.data && (
        <div className="mt-3 border-t border-bg-border pt-3">
          <tool.Body ctx={ctx} />
        </div>
      )}
    </Shell>
  );
}

// ── WellFinder's own card body ──────────────────────────────────────────────
//
// Two things, in this order: what is waiting on YOU, then where the corpus
// stands. The first decides whether to open the tool at all; the second is the
// context that makes the first number mean something.
//
// The per-record deep links that used to live in "What needs you" are NOT here.
// A card is a summary and a link to one place; six links to six records is the
// review surface's job, and that is where these counts send you.
function WellfinderCardBody({ ctx }) {
  const { me, totals, remaining } = ctx;
  const claims = me?.claims ?? [];
  const reopened = me?.reopened?.length ?? 0;

  return (
    <>
      {(claims.length > 0 || reopened > 0) && (
        <div className="mb-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {claims.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-amber-300">
              <Hand size={12} /> {fmt(claims.length)} in progress
            </span>
          )}
          {reopened > 0 && (
            <span className="inline-flex items-center gap-1.5 text-amber-300">
              <AlertTriangle size={12} /> {fmt(reopened)} reopened by someone else
            </span>
          )}
        </div>
      )}

      {/* A first session is pinned to Tier A regardless of what is thinnest, so
          the card says so rather than letting the tier lines imply a choice. */}
      {!me?.has_history && (
        <p className="mb-2.5 text-xs text-fg-secondary">
          First session — starts on Tier {TIER_LABEL[FIRST_SESSION_TIER] ?? FIRST_SESSION_TIER}.
        </p>
      )}

      <p className="text-xs">
        <span className="font-medium text-fg-primary">{fmt(totals.adjudicated)}</span>
        <span className="text-fg-secondary"> of {fmt(totals.total)} adjudicated</span>
      </p>

      <div className="mt-2 space-y-1.5">
        {TIER_ORDER.map((t) => (
          <TierLine
            key={t}
            tier={t}
            remaining={remaining[t] ?? 0}
            // Tier C carries its own emphasis. It is the differentiated product
            // and the most likely thing to be neglected in a queue that is 87%
            // mechanical, so it does not get to look like a rounding error next
            // to Tier A's thousands.
            emphasis={t === "c"}
          />
        ))}
      </div>
    </>
  );
}

function Section({ title, children, panel = false }) {
  if (panel) {
    return (
      <section className="rounded-lg border border-bg-border bg-white/[0.02] px-4 py-3.5">
        <h2 className="text-[11px] font-medium font-mono uppercase tracking-widest text-fg-secondary">
          {title}
        </h2>
        <div className="mt-2.5">{children}</div>
      </section>
    );
  }
  return (
    <section className="mt-9 border-t border-bg-border pt-5">
      <h2 className="text-[11px] font-medium font-mono uppercase tracking-widest text-fg-secondary">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}



function TierLine({ tier, remaining, emphasis }) {
  return (
    <div
      className={[
        "flex items-baseline gap-2 text-sm",
        emphasis ? "rounded border border-accent-info/25 bg-accent-info/5 px-2.5 py-1.5" : "",
      ].join(" ")}
    >
      <span className={emphasis ? "font-medium text-accent-info" : "text-fg-secondary"}>
        Tier {TIER_LABEL[tier] ?? tier}
      </span>
      <span className={emphasis ? "text-fg-primary" : "text-fg-secondary"}>
        {remaining > 0 ? `${fmt(remaining)} remaining` : "clear"}
      </span>
      {/* The reason Tier C is emphasised, as a title rather than trailing
          text: in a 21rem rail the sentence wrapped to three lines and made
          this panel taller than the three beside it, which is a lot of vertical
          space spent restating what the emphasis already says. */}
      {emphasis && remaining > 0 && (
        <span
          className="cursor-help text-xs text-fg-secondary"
          title="The differentiated product, and the most likely thing to be neglected in a queue that is 87% mechanical."
        >
          — easy to leave until last
        </span>
      )}
    </div>
  );
}

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

function NeedsYouRow({ icon, label, count, to, search, cta, empty }) {
  const has = (count ?? 0) > 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="shrink-0">{icon}</span>
      <span className="text-sm">
        {has ? (
          <>
            <span className="font-medium text-fg-primary">{fmt(count)}</span>
            <span className="text-fg-secondary"> · {label}</span>
          </>
        ) : (
          <span className="text-fg-secondary">{empty}</span>
        )}
      </span>
      <div className="flex-1" />
      {has && (
        <Link
          to={to}
          search={search}
          className="shrink-0 text-xs text-accent-info transition-colors hover:text-white"
        >
          {cta}
        </Link>
      )}
    </div>
  );
}

/**
 * The reopened decisions — the REASON, with the record attached.
 *
 * ── WHY NOT A LINK TO A FILTERED QUEUE ───────────────────────────────────────
 *
 * Because there is no such filter. This used to say "Review them" and link to
 * /workspace/review?status=pending, which is not the three reopened records — it
 * is the whole pending queue with the three somewhere inside and nothing marking
 * them. The link answered a different question than the label asked, which is
 * worse than no link: the count was right, so the page looked like it worked.
 *
 * A server-side "mine, later reopened" filter would be the general fix and is
 * not worth it for a list /me/summary returns in full.
 *
 * ── THE NOTE IS THE POINT ────────────────────────────────────────────────────
 *
 * Reopening requires a reason — validateNote({required:true}) on the reopen
 * route — so every one of these carries a written statement of why a verdict was
 * withdrawn. That statement is the only thing here anyone can act on, so it is
 * the body of each row rather than something to go and find.
 *
 * WHO reopened it is shown for a reason that turned out to matter: in the live
 * corpus all three of these were reopened by the analyst themselves, minutes
 * after committing. A self-undo is not someone disagreeing with you, and without
 * the name this panel would read as though it were.
 */
function ReopenedRows({ reopened }) {
  if (!reopened.length) {
    return (
      <p className="text-sm text-fg-secondary">
        None of your decisions have been reopened.
      </p>
    );
  }

  return (
    // BOUNDED AND SCROLLABLE. The server caps this at 50, and 50 rows of record
    // plus quoted reason is several screens — enough to push the primary action
    // off the top of a laptop viewport, which is the one thing this page must
    // not do. max-h rather than a "show more" control: the count is in the
    // heading, so nothing is hidden, and a scrollbar is a cheaper affordance
    // than a disclosure for a list most people read once.
    //
    // pr-1 leaves room for the scrollbar so it does not sit on the text, and
    // -mr-1 gives that back to the column so the section still lines up with
    // the ones above and below it.
    <ul className="max-h-64 -mr-1 space-y-2.5 overflow-y-auto pr-1">
      {reopened.map((r) => (
        <li key={r.record_id} className="text-xs">
          <div className="flex items-baseline gap-1.5">
            <span className="shrink-0 rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-fg-secondary">
              {TIER_LABEL[r.tier] ?? r.tier}
            </span>
            <Link
              to="/workspace/review"
              search={{ record: r.record_id, status: "pending" }}
              className="min-w-0 truncate text-accent-info transition-colors hover:text-white"
              title={r.source_id ?? r.record_id}
            >
              {r.source_id ?? r.record_id}
            </Link>
          </div>

          {/* The reason. Quoted rather than paraphrased — it is somebody's
              sentence, and the rail is where there is room to read it. */}
          {r.reopened_note && (
            <p className="mt-1 border-l-2 border-bg-border pl-2 italic text-fg-secondary">
              {r.reopened_note}
            </p>
          )}

          <p className="mt-1 text-[11px] text-fg-secondary/80">
            {r.reopened_by_name ? `Reopened by ${r.reopened_by_name}` : "Reopened"}
            {r.reopened_at && ` · ${new Date(r.reopened_at).toLocaleDateString()}`}
          </p>
        </li>
      ))}
    </ul>
  );
}
